import { Star, ChevronRight, ShieldCheck, Share2, AudioLines, Compass, GitBranch, RefreshCcw, Music2, Info } from "lucide-react";

/* ---------- Phone-frame components (static mock screens) ---------- */

const PhoneFrame = ({ children }) => (
  <div className="relative w-[280px] h-[605px] rounded-[44px] bg-black border-[10px] border-[#1a1a1a] shadow-2xl shrink-0 overflow-hidden">
    {/* Dynamic island */}
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-black z-30" />
    {/* Status bar */}
    <div className="absolute top-0 left-0 right-0 h-10 flex items-center justify-between px-7 z-20 text-white text-[11px] font-semibold">
      <span>9:41</span>
      <div className="flex items-center gap-1">
        <span>●●●●</span>
        <span>􀟈</span>
        <span>􀛨</span>
      </div>
    </div>
    <div className="absolute inset-0 bg-[#030303] pt-10">{children}</div>
  </div>
);

const ScreenUpload = () => (
  <div className="h-full w-full px-4 pt-3 flex flex-col">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-1.5">
        <div className="w-7 h-7 rounded-lg bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
          <AudioLines className="w-3.5 h-3.5 text-[#FF2D55]" strokeWidth={2.2} />
        </div>
        <span className="font-display font-black text-white text-base tracking-tight">KeyFinder</span>
      </div>
      <ShieldCheck className="w-3 h-3 text-[#39FF14]" />
    </div>
    <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-4 shadow-2xl">
      <h1 className="font-display text-2xl font-black tracking-tighter text-white leading-[1] mb-1.5">
        Find the<br /><span className="text-[#FF2D55] led-glow">key</span> of any song
      </h1>
      <p className="text-[10px] text-zinc-500 leading-relaxed">Drop an audio file. Get the key in seconds.</p>
      <div className="flex flex-col items-center justify-center border-2 border-dashed border-white/10 bg-[#0F0F13] rounded-xl p-5 mt-3">
        <div className="w-10 h-10 rounded-xl bg-[#0A0A0A] border border-white/10 flex items-center justify-center mb-2">
          <svg className="w-4 h-4 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" /></svg>
        </div>
        <div className="text-xs font-semibold text-white">Drop audio here</div>
        <div className="text-[9px] text-zinc-500 mt-0.5">or <span className="text-[#FF2D55]">browse files</span></div>
        <div className="text-[8px] uppercase tracking-[0.18em] text-zinc-600 font-mono mt-2">mp3 · wav · m4a · max 100mb</div>
      </div>
      <div className="mt-3 w-full bg-[#FF2D55]/20 text-[#FF2D55] font-bold py-2.5 rounded-full text-xs text-center font-display shadow-[0_0_20px_rgba(255,45,85,0.2)]">
        ✦ Analyze key
      </div>
    </div>
  </div>
);

const ScreenResult = () => (
  <div className="h-full w-full px-4 pt-3 flex flex-col">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-1.5">
        <div className="w-7 h-7 rounded-lg bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
          <AudioLines className="w-3.5 h-3.5 text-[#FF2D55]" strokeWidth={2.2} />
        </div>
        <span className="font-display font-black text-white text-base tracking-tight">KeyFinder</span>
      </div>
      <ShieldCheck className="w-3 h-3 text-[#39FF14]" />
    </div>
    <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-4 shadow-2xl flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[8px] uppercase tracking-[0.22em] text-zinc-600 font-semibold">Detected from</div>
          <div className="text-[10px] text-zinc-300 truncate">midnight_drive.mp3</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
          <span className="text-[9px] uppercase tracking-[0.18em] font-mono font-semibold text-[#39FF14]">High · 94%</span>
        </div>
      </div>
      <div className="bg-[#0F0F13] border border-white/5 rounded-xl py-5 flex flex-col items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF2D55]/10 to-transparent pointer-events-none" />
        <div className="text-[8px] uppercase tracking-[0.3em] text-zinc-500 font-mono font-semibold mb-1">musical key</div>
        <div className="font-display text-4xl font-black tracking-tighter text-white led-glow">F# minor</div>
        <div className="mt-2 flex items-center gap-1 text-[9px] text-zinc-500">
          <Music2 className="w-2.5 h-2.5" /> <span>Tonal center estimated</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-[#0F0F13] border border-white/5 rounded-xl p-2.5">
          <div className="flex items-center gap-1 text-[8px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">
            <Compass className="w-2.5 h-2.5 text-[#39FF14]" /> Camelot
          </div>
          <div className="font-mono text-xl text-[#39FF14] font-bold led-glow-green leading-none">11A</div>
          <div className="text-[8px] text-zinc-600 mt-0.5">DJ mix wheel</div>
        </div>
        <div className="bg-[#0F0F13] border border-white/5 rounded-xl p-2.5">
          <div className="flex items-center gap-1 text-[8px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">
            <GitBranch className="w-2.5 h-2.5 text-[#FF2D55]" /> Relative
          </div>
          <div className="font-mono text-sm text-[#FF2D55] font-semibold leading-none pt-1">A major</div>
          <div className="text-[8px] text-zinc-600 mt-1">Same notes, diff. mood</div>
        </div>
      </div>
      <div className="bg-[#0F0F13] border border-white/5 rounded-xl p-2.5">
        <div className="flex items-center gap-1 text-[8px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">
          <Info className="w-2.5 h-2.5" /> In plain English
        </div>
        <p className="text-[9px] text-zinc-300 leading-relaxed">
          This song sits around F# minor — moody and emotional. Great for vocal harmonies and dark synth leads.
        </p>
      </div>
      <div className="w-full bg-white/5 border border-white/5 text-white text-[10px] py-2 rounded-full text-center font-display flex items-center justify-center gap-1">
        <RefreshCcw className="w-2.5 h-2.5" /> Analyze another song
      </div>
    </div>
  </div>
);

const ScreenHistory = () => {
  const items = [
    { song: "midnight_drive.mp3", key: "F# minor", rel: "A major", cam: "11A", conf: "High", time: "12s" },
    { song: "summer_walk.wav", key: "C major", rel: "A minor", cam: "8B", conf: "High", time: "3m" },
    { song: "rainy_jazz.m4a", key: "Bb major", rel: "G minor", cam: "6B", conf: "Med", time: "18m" },
    { song: "deep_house_001.mp3", key: "A minor", rel: "C major", cam: "8A", conf: "High", time: "2h" },
  ];
  const conf = (c) => (c === "High" ? "text-[#39FF14]" : c === "Med" ? "text-[#FFB800]" : "text-[#FF6666]");
  return (
    <div className="h-full w-full px-4 pt-3 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <div className="w-7 h-7 rounded-lg bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
            <AudioLines className="w-3.5 h-3.5 text-[#FF2D55]" strokeWidth={2.2} />
          </div>
          <span className="font-display font-black text-white text-base tracking-tight">KeyFinder</span>
        </div>
        <ShieldCheck className="w-3 h-3 text-[#39FF14]" />
      </div>
      <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-3 shadow-2xl">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-[#0F0F13] border border-white/10 flex items-center justify-center">
            <svg className="w-3 h-3 text-[#FF2D55]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" strokeWidth={2}/><path strokeLinecap="round" strokeWidth={2} d="M12 7v5l3 2"/></svg>
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-bold text-white text-[11px]">Recent analyses</span>
            <span className="text-[8px] uppercase tracking-[0.18em] text-zinc-500 font-mono">4 songs</span>
          </div>
        </div>
        <ul className="flex flex-col gap-1.5">
          {items.map((it, i) => (
            <li key={i} className="bg-[#0F0F13] border border-white/5 rounded-xl p-2 flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#0A0A0A] border border-white/10 flex flex-col items-center justify-center shrink-0">
                <span className="font-mono text-[9px] text-[#39FF14] font-bold leading-none">{it.cam}</span>
                <span className="font-display text-[8px] text-zinc-500 leading-none mt-0.5">Cam</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-white truncate">{it.song}</div>
                <div className="flex items-center gap-1 text-[8px] text-zinc-500 font-mono mt-0.5">
                  <span className="text-[#FF2D55] font-semibold">{it.key}</span>
                  <span className="text-zinc-700">·</span>
                  <span>{it.rel}</span>
                  <span className="text-zinc-700">·</span>
                  <span className={conf(it.conf)}>{it.conf}</span>
                </div>
              </div>
              <span className="text-[8px] text-zinc-600 font-mono">{it.time}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ---------- App Store Layout ---------- */

export default function AppStorePreview() {
  return (
    <div className="min-h-screen w-full bg-white text-black">
      {/* Top status bar (iOS look) */}
      <div className="max-w-[1100px] mx-auto px-6 pt-3 pb-1 flex items-center justify-between text-[13px] font-semibold text-black">
        <span>9:41</span>
        <div className="flex items-center gap-1">
          <span>●●●●</span>
          <span>5G</span>
          <span>􀛨</span>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 pb-12">
        {/* Navigation header */}
        <div className="flex items-center justify-between py-3 border-b border-zinc-200">
          <div className="flex items-center gap-1 text-[#0a84ff] text-[15px] font-medium">
            <ChevronRight className="w-4 h-4 rotate-180" />
            Today
          </div>
          <Share2 className="w-5 h-5 text-[#0a84ff]" />
        </div>

        {/* App header */}
        <div className="flex items-center gap-5 py-6">
          {/* App icon */}
          <div className="w-32 h-32 rounded-[28px] bg-[#030303] border border-zinc-200 shrink-0 relative overflow-hidden flex items-center justify-center shadow-lg">
            <div className="absolute inset-0 opacity-50" style={{background: "radial-gradient(circle at 50% 30%, rgba(255,45,85,0.45), transparent 60%)"}} />
            <div className="relative z-10 flex flex-col items-center">
              <AudioLines className="w-12 h-12 text-[#FF2D55]" strokeWidth={2.4} />
              <span className="font-display font-black text-white text-base tracking-tight mt-1">Key</span>
              <span className="font-mono text-[9px] text-[#39FF14] font-bold tracking-[0.2em] mt-0.5">FINDER</span>
            </div>
          </div>
          {/* Title block */}
          <div className="flex-1 flex flex-col gap-2">
            <h1 className="font-display text-[28px] font-black text-black leading-tight tracking-tight">KeyFinder</h1>
            <p className="text-[15px] text-zinc-600 leading-snug">AI musical key detector for songs</p>
            <div className="flex items-center gap-2 mt-1">
              <button className="bg-[#0a84ff] text-white text-[15px] font-bold px-7 py-1.5 rounded-full">GET</button>
              <span className="text-[11px] text-zinc-500 ml-1">In-App Purchases</span>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 border-y border-zinc-200 py-4 text-center">
          <div className="border-r border-zinc-200">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Rating</div>
            <div className="text-xl font-bold text-zinc-900 mt-0.5">4.9</div>
            <div className="flex items-center justify-center gap-0.5 mt-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-2.5 h-2.5 fill-zinc-700 text-zinc-700" />)}
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">12.4K Ratings</div>
          </div>
          <div className="border-r border-zinc-200">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Age</div>
            <div className="text-xl font-bold text-zinc-900 mt-0.5">4+</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">Years Old</div>
          </div>
          <div className="border-r border-zinc-200">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Chart</div>
            <div className="text-xl font-bold text-zinc-900 mt-0.5">#3</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">Music</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Developer</div>
            <div className="text-base font-semibold text-[#0a84ff] mt-1.5">KeyFinder</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">Audio Utility</div>
          </div>
        </div>

        {/* Preview heading */}
        <div className="pt-6 pb-3">
          <h2 className="font-display text-[22px] font-bold text-black tracking-tight">Preview</h2>
        </div>

        {/* Screenshots scroller */}
        <div className="flex gap-4 overflow-hidden">
          <PhoneFrame><ScreenUpload /></PhoneFrame>
          <PhoneFrame><ScreenResult /></PhoneFrame>
          <PhoneFrame><ScreenHistory /></PhoneFrame>
        </div>

        {/* Description */}
        <div className="pt-8">
          <p className="text-[14px] text-zinc-800 leading-relaxed font-body">
            <span className="font-bold">KeyFinder finds the musical key of any song in seconds.</span> Drop in an audio file (MP3, WAV, FLAC, M4A…) and instantly get the detected key, Camelot DJ notation, the relative major/minor, and a plain-language explanation. Built for DJs, producers, singers, students, and curious listeners.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-6 text-[12px]">
            <div className="flex flex-col gap-1">
              <span className="font-display font-bold text-black">Studio-grade detection</span>
              <span className="text-zinc-600">Krumhansl-Schmuckler chroma analysis on the harmonic component.</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-display font-bold text-black">Privacy first</span>
              <span className="text-zinc-600">Files are analyzed and deleted immediately. Only key results are saved.</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-display font-bold text-black">Built for DJs</span>
              <span className="text-zinc-600">Camelot wheel notation and relative-key suggestions for smooth mixing.</span>
            </div>
          </div>
        </div>

        <div className="text-[11px] text-zinc-400 mt-10 text-center font-mono uppercase tracking-[0.2em]">
          App Store Preview · KeyFinder 1.0
        </div>
      </div>
    </div>
  );
}
